/**
 * api/config.ts
 * Resolves the SK Queue API base URL.
 *
 * In the browser we default to the SAME-ORIGIN proxy prefix `/queue-api` (the
 * webpack dev-server forwards it to the local sk-queue-api; in production the
 * backbone gateway serves it same-origin too). This avoids CORS entirely — the
 * backend ships no CORS headers, and same-origin matches the real topology.
 *
 * An explicit absolute URL can be forced via QUEUE_API_URL (build-time
 * DefinePlugin) for native/non-proxied setups.
 */

import { Platform } from 'react-native';

declare const process: { env?: { QUEUE_API_URL?: string } };

function readEnv(): string | undefined {
  try {
    return process.env?.QUEUE_API_URL;
  } catch {
    return undefined;
  }
}

function resolveBase(): string {
  const explicit = readEnv();
  if (explicit) return explicit.replace(/\/$/, '');
  // Web: same-origin proxy prefix (no CORS). Native: direct to the dev port.
  if (Platform.OS === 'web') return '/queue-api';
  return 'http://localhost:3070';
}

/** Base for the queue API. May be a same-origin path ('/queue-api') or absolute URL. */
export const QUEUE_API_BASE_URL: string = resolveBase();

/** Unversioned health probe (sk-queue-api mounts /health outside /v1). */
export const QUEUE_HEALTH_URL = `${QUEUE_API_BASE_URL}/health`;

/** Versioned API root (all product routes mount under /v1 behind auth). */
export const QUEUE_V1 = `${QUEUE_API_BASE_URL}/v1`;
